import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import { useTranslation } from "react-i18next";
import mapMarker from "assets/images/map-marker.svg";
import MapPinPopup from "./MapPinPopup";
import { useSelector } from "react-redux";
import BookAppointmentModal from "./BookAppointmentModal";
import useHandleApiError from "hooks/useHandleApiError";
import { useGetMembersForBooking } from "repositories/family-member-repository";

const DEFAULT_CENTER = { lat: 0, lng: 0 };
const ZOOM = 14;
const MAP_MIN_HEIGHT = 880;

/**
 * Map UI for doctors search. Uses the official Maps JS API via @googlemaps/react-wrapper
 * instead of google-maps-react, which relies on removed ReactDOM.findDOMNode (React 19+).
 */
function DoctorsMapInner({
  center,
  items,
  activeMarker,
  setActiveMarker,
  clickHandlers,
}) {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const infoRootRef = useRef(null);

  const profile = useSelector((state) => state.userProfile.profile);

  const PAGE_SIZE = 2;
  const memberPageNumber = 1;
  const {
    data,
    error: isError,
    isLoading,
    isFetching,
  } = useGetMembersForBooking(memberPageNumber, PAGE_SIZE, {
    enabled: !!profile?.id,
  });

  useHandleApiError(isLoading, isFetching, isError);

  const [isBookAppointmentModalOpen, setIsBookAppointmentModalOpen] =
    useState(false);
  const [appointmentType, setAppointmentType] = useState("");

  const openBookAppointmentPopUp = useCallback((type) => {
    setIsBookAppointmentModalOpen(true);
    setAppointmentType(type);
  }, []);

  const { handleBookAppointment, handleRequestAppointment } = clickHandlers;

  const handleInfoPopupClose = useCallback(() => {
    setActiveMarker(null);
  }, [setActiveMarker]);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps?.Map) return undefined;

    const initialCenter =
      center?.lat != null && center?.lng != null ? center : DEFAULT_CENTER;

    const instance = new window.google.maps.Map(mapRef.current, {
      disableDefaultUI: true,
      zoomControl: true,
      scaleControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.TOP_LEFT,
      },
      center: initialCenter,
      zoom: ZOOM,
    });

    const onMapClick = () => {
      setActiveMarker(null);
    };
    const mapClickListener = instance.addListener("click", onMapClick);

    setMap(instance);

    return () => {
      window.google.maps.event.removeListener(mapClickListener);
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      if (infoRootRef.current) {
        infoRootRef.current.unmount();
        infoRootRef.current = null;
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      setMap(null);
    };
    // Map is created once; center updates use a separate effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map || !center || center.lat == null || center.lng == null) return;
    map.setCenter(center);
  }, [map, center]);

  useEffect(() => {
    if (!map || !window.google?.maps?.Marker) return undefined;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    (items || []).forEach((marker) => {
      const lat = marker?.location?.lat;
      const lng = marker?.location?.lng;
      if (lat == null || lng == null) return;

      const m = new window.google.maps.Marker({
        map,
        position: { lat, lng },
        animation: window.google.maps.Animation.DROP,
        icon: mapMarker,
      });
      m.addListener("click", () => {
        setActiveMarker(marker);
      });
      markersRef.current.push(m);
    });

    return () => {
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
    };
  }, [items, map, setActiveMarker]);

  useEffect(() => {
    if (!map) return undefined;

    if (infoWindowRef.current) {
      window.google.maps.event.clearInstanceListeners(infoWindowRef.current);
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }
    if (infoRootRef.current) {
      infoRootRef.current.unmount();
      infoRootRef.current = null;
    }

    if (!activeMarker?.location) return undefined;

    const lat = activeMarker.location.lat;
    const lng = activeMarker.location.lng;
    if (lat == null || lng == null) return undefined;

    const container = document.createElement("div");
    container.id = "map-info-popover";

    const infoWindow = new window.google.maps.InfoWindow({
      content: container,
      maxWidth: 400,
      pixelOffset: new window.google.maps.Size(0, -47),
    });

    infoWindow.setPosition({ lat, lng });
    infoWindow.open(map);

    const root = createRoot(container);
    infoRootRef.current = root;
    infoWindowRef.current = infoWindow;

    root.render(
      <MapPinPopup
        activeMarker={activeMarker}
        handleInfoPopupClose={handleInfoPopupClose}
        clickHandlers={clickHandlers}
        profile={profile}
        openBookAppointmentPopUp={openBookAppointmentPopUp}
        data={data}
      />
    );

    const closeListener = infoWindow.addListener("closeclick", () => {
      setActiveMarker(null);
    });

    return () => {
      window.google.maps.event.removeListener(closeListener);
      if (infoRootRef.current) {
        infoRootRef.current.unmount();
        infoRootRef.current = null;
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
    };
  }, [
    activeMarker,
    clickHandlers,
    data,
    handleInfoPopupClose,
    map,
    openBookAppointmentPopUp,
    profile,
    setActiveMarker,
  ]);

  return (
    <>
      <div
        ref={mapRef}
        style={{ width: "100%", height: "100%", minHeight: MAP_MIN_HEIGHT }}
      />
      {isBookAppointmentModalOpen && (
        <BookAppointmentModal
          isBookAppointmentModalOpen={isBookAppointmentModalOpen}
          setIsBookAppointmentModalOpen={setIsBookAppointmentModalOpen}
          loggedInUserId={profile?.id}
          handleMemberClick={(memberId) => {
            appointmentType === "bookAppointment"
              ? handleBookAppointment(activeMarker, memberId)
              : handleRequestAppointment(activeMarker, memberId);
          }}
        />
      )}
    </>
  );
}

export default function DoctorsMap(props) {
  const { t } = useTranslation();

  const renderMapStatus = useCallback(
    (status) => {
      if (status === Status.LOADING) {
        return (
          <div className="center" style={{ minHeight: MAP_MIN_HEIGHT }}>
            <div className="loader" />
          </div>
        );
      }
      if (status === Status.FAILURE) {
        return (
          <div className="center" style={{ minHeight: MAP_MIN_HEIGHT }}>
            {t("somethingWentWrong")}
          </div>
        );
      }
      return null;
    },
    [t]
  );

  return (
    <Wrapper
      apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
      render={renderMapStatus}
    >
      <DoctorsMapInner {...props} />
    </Wrapper>
  );
}
